import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelection } from '@/app/SelectionContext';
import { useAuth } from '@/app/AuthContext';
import { PageHeader } from '@/components/PageHeader';
import { LoadingScreen } from '@/components/LoadingScreen';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { businessUnitsService } from '@/services/businessUnits.service';
import { ApiRequestError } from '@/services/api';
import { UNIT_TYPE_LABELS, type BusinessUnitType } from '@/types/businessUnit';
import { Permission } from '@/types/permission';
import { v } from '@/theme/tokens';

const CREATABLE_TYPES: BusinessUnitType[] = [
  'RESTAURANT',
  'PRODUCTION_KITCHEN',
  'CAFE',
  'BAKERY',
  'WAREHOUSE',
  'OTHER',
];

export function BusinessUnitsPage() {
  const { units, loading, error, reload, setScope } = useSelection();
  const { can } = useAuth();
  const navigate = useNavigate();

  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<BusinessUnitType>('RESTAURANT');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const onCreate = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await businessUnitsService.create({ name, type, status: 'OPENING' });
      setName('');
      setFormOpen(false);
      reload();
    } catch (e) {
      setFormError(e instanceof ApiRequestError ? e.message : 'Не удалось создать объект');
    } finally {
      setSubmitting(false);
    }
  };

  const openUnit = (id: string, unitType: BusinessUnitType) => {
    setScope(id);
    navigate(unitType === 'PRODUCTION_KITCHEN' ? '/production' : '/');
  };

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Объекты"
        subtitle="Бизнес-единицы компании"
        actions={
          can(Permission.CREATE_BUSINESS_UNIT) && (
            <Button variant="primary" onClick={() => setFormOpen((o) => !o)}>
              <Icon name="plus" size={15} />
              Новый объект
            </Button>
          )
        }
      />

      {formOpen && (
        <Card style={{ marginBottom: 16 }}>
          <form onSubmit={onCreate} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label htmlFor="unit-name" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
                Название
              </label>
              <input
                id="unit-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginTop: 6,
                  border: `1px solid ${v('line')}`,
                  borderRadius: 10,
                  background: v('surface'),
                  color: v('ink'),
                  fontSize: 14,
                }}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <label htmlFor="unit-type" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
                Тип
              </label>
              <select
                id="unit-type"
                value={type}
                onChange={(e) => setType(e.target.value as BusinessUnitType)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  marginTop: 6,
                  border: `1px solid ${v('line')}`,
                  borderRadius: 10,
                  background: v('surface'),
                  color: v('ink'),
                  fontSize: 14,
                }}
              >
                {CREATABLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {UNIT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" variant="primary" disabled={submitting}>
              Создать
            </Button>
          </form>
          {formError && (
            <div style={{ color: v('negative'), fontSize: 13, marginTop: 10 }}>{formError}</div>
          )}
        </Card>
      )}

      {units.length === 0 ? (
        <EmptyState
          title="Объекты не созданы"
          description="Запустите «python -m app.cli seed-core» на сервере, чтобы создать структуру компании."
          icon="building"
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 16 }}>
          {units.map((unit) => (
            <Card key={unit.id} padding={22}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => openUnit(unit.id, unit.type)}
                onKeyDown={(e) => e.key === 'Enter' && openUnit(unit.id, unit.type)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: v('accent-soft'),
                      color: v('accent'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={unit.type === 'PRODUCTION_KITCHEN' ? 'factory' : 'building'} size={22} />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{unit.name}</div>
                    <Badge tone="neutral">{UNIT_TYPE_LABELS[unit.type]}</Badge>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: v('ink-muted') }}>
                  {unit.status === 'OPENING'
                    ? 'Открывается — данных ещё нет'
                    : 'Показатели появятся после импорта отчёта'}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
